import React, { useState } from 'react'
import { extent, max } from 'd3-array'
import { scaleLinear, scaleTime } from 'd3-scale'
import useDimensions from 'hooks/useDimensions'
import './styles.scss'
import { area } from 'd3-shape'
const SCHOOL_DATA_RAW: {}[] = require('../schooldata.json')
const NO_DATE_PROPS = ['code', 'namede', 'namefr', 'rank']
const DATES = Object.keys(SCHOOL_DATA_RAW[0]).filter(
  f => NO_DATE_PROPS.indexOf(f) === -1
)
/* istanbul ignore next */

const SCHOOL_DATA = Array.from(SCHOOL_DATA_RAW).map(country => {
  const data: { x: string; y: number }[] = []
  DATES.forEach(date => {
    data.push({ x: date, y: +country[date] })
  })
  return {
    namede: country.namede,
    namefr: country.namefr,
    code: country.code,
    data
  }
})
const DATE_EXTENT = max(DATES, d => new Date(d))
const X = scaleTime().domain([new Date('2020-01-01'), DATE_EXTENT] as [
  Date,
  Date
])
const Y = scaleLinear().domain([0, 3])

var areaGenerator = area()
  .x(function(d) {
    return X(new Date(d.x))
  })
  .y0(0)
  .y1(function(d) {
    return 0 - Y(d.y)
  })

const YSPACE = 50
const YSTART = 40
const XSTART = 120

export const StepChart = ({
  locale,
  locales
}: {
  locale: 'DE' | 'FR'
  locales: { title: string; sub: string; options: string[] }
}) => {
  const [tt, setTT] = useState()
  const [ref, size, node] = useDimensions()
  X.range([XSTART, size.width])
  Y.range([0, YSPACE * 0.6])
  return (
    <div className='StepChart'>
      <div>
        <h4>{locales.title}</h4>
        <div>{locales.sub}</div>
        <div className='optionscontainer'>
          {locales.options.map((m, i) => (
            <div key={i}>{m}</div>
          ))}
        </div>
      </div>
      <svg
        ref={ref}
        onMouseMove={e => {
          const x = e.nativeEvent.offsetX
          if (x > XSTART) {
            setTT({ x })
          } else {
            setTT(undefined)
          }
        }}
        onMouseLeave={() => setTT(undefined)}
        height={(SCHOOL_DATA.length + 0.5) * YSPACE + YSTART}
      >
        {SCHOOL_DATA.map((country, i) => {
          const y = (i + 1) * YSPACE + YSTART
          return (
            <g transform={`translate(0,${y})`} key={i}>
              <line
                x1={XSTART}
                x2={size.width}
                y1={Y(4) - YSPACE}
                y2={Y(4) - YSPACE}
                className={'ytick'}
              />
              <line
                x1={XSTART}
                x2={size.width}
                y1={Y(3) - YSPACE}
                y2={Y(3) - YSPACE}
                className={'ytick'}
              />
              <line
                x1={XSTART}
                x2={size.width}
                y1={Y(2) - YSPACE}
                y2={Y(2) - YSPACE}
                className={'ytick'}
              />
              {i === 0 && (
                <>
                  {' '}
                  <text
                    x={XSTART - 4}
                    y={Y(2) - YSPACE + 3}
                    textAnchor='end'
                    className='label'
                  >
                    3
                  </text>
                  <text
                    x={XSTART - 4}
                    y={Y(5) - YSPACE + 3}
                    textAnchor='end'
                    className='label'
                  >
                    0
                  </text>
                </>
              )}

              <text
                y={-YSPACE * 0.35}
                className={country.code === 'CH' ? 'bold' : ''}
              >
                {locale === 'FR' ? country.namefr : country.namede}
              </text>
              <path
                d={areaGenerator(country.data)}
                className={country.code === 'CH' ? 'ch' : ''}
              />
              <line x1={XSTART} x2={size.width} />
            </g>
          )
        })}
        {['2020', '2021'].map(year => (
          <XTick
            key={year}
            x={X(new Date(year + '-01-01'))}
            y={SCHOOL_DATA.length * YSPACE + 17 + YSTART}
            text={year + ''}
          />
        ))}
        {tt && tt.x && (
          <g transform={`translate(${tt.x},0)`}>
            <line
              y1={YSTART + 4}
              y2={SCHOOL_DATA.length * YSPACE + YSTART}
              stroke={'#f00'}
              strokeWidth={2}
            />
            <text
              y={YSTART}
              textAnchor={tt.x < size.width * 0.6 ? 'start' : 'end'}
            >
              {X.invert(tt.x).toLocaleDateString(
                locale === 'FR' ? 'fr-CH' : 'de-CH',
                {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }
              )}
            </text>
          </g>
        )}
      </svg>
      <div className='source'>
        {locale === 'FR' ? 'Source:' : 'Quelle:'}{' '}
        <a
          href='https://github.com/OxCGRT/covid-policy-tracker/blob/master/documentation/codebook.md'
          target='_blank'
        >
          {' '}
          University of Oxford
        </a>
      </div>
    </div>
  )
}
const XTick = ({ x, y, text }: { x: number; y: number; text: string }) => {
  return (
    <>
      <line x1={x} y1={y - 16} x2={x} y2={y + 1} className='label' />
      <text x={x + 4} y={y} className='label'>
        {text}
      </text>
    </>
  )
}
